import {
  useToggleNotification,
  useUpdateDateFormat,
  useUpdateLanguage,
  useUpdateTimezone,
} from '@/hooks/useUserPreferences';
import { useAuthStore } from '@/store/auth.store';
import { Bell, Globe, Loader2, User } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Toggle Switch Component
const ToggleSwitch = ({
  checked,
  onChange,
  disabled = false,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
}) => {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`focus:ring-primary-500 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none ${
        checked ? 'bg-primary-600' : 'bg-neutral-300'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

// Select Component
const Select = ({
  value,
  onChange,
  options,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) => {
  return (
    <select
      value={value}
      onChange={e => !disabled && onChange(e.target.value)}
      disabled={disabled}
      className={`focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white px-4 py-2 text-sm transition-colors focus:ring-2 ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Loading Indicator
const UpdatingIndicator = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-primary-50 text-primary-700 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm">
      <Loader2 className="h-3 w-3 animate-spin" />
      {t('settings.userPreferences.saving', 'Enregistrement…')}
    </div>
  );
};

export function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  // Hooks for updating preferences
  const toggleEmailNotifications = useToggleNotification('email');
  const togglePushNotifications = useToggleNotification('push');
  const updateLanguageMutation = useUpdateLanguage();
  const updateTimezoneMutation = useUpdateTimezone();
  const updateDateFormatMutation = useUpdateDateFormat();

  if (!user) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="space-y-3 text-center">
          <div className="border-primary-600 mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"></div>
          <p className="text-neutral-600">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  const handleLanguageChange = (language: string) => {
    setIsUpdating('language');
    updateLanguageMutation(language);
    // Reset updating state after a short delay (the mutation will show success/error toast)
    setTimeout(() => setIsUpdating(null), 1000);
  };

  const handleTimezoneChange = (timezone: string) => {
    setIsUpdating('timezone');
    updateTimezoneMutation(timezone);
    setTimeout(() => setIsUpdating(null), 1000);
  };

  const handleDateFormatChange = (dateFormat: string) => {
    setIsUpdating('dateFormat');
    updateDateFormatMutation(dateFormat);
    setTimeout(() => setIsUpdating(null), 1000);
  };

  const handleNotificationToggle = (
    type: 'email' | 'push',
    enabled: boolean
  ) => {
    setIsUpdating(type + 'Notifications');
    if (type === 'email') {
      toggleEmailNotifications(enabled);
    } else {
      togglePushNotifications(enabled);
    }
    setTimeout(() => setIsUpdating(null), 1000);
  };

  // Available options
  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
  ];

  const timezoneOptions = [
    // Europe
    { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
    { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (UTC+1)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (UTC+1)' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (UTC+1)' },
    { value: 'Europe/Brussels', label: 'Europe/Brussels (UTC+1)' },
    { value: 'Europe/Vienna', label: 'Europe/Vienna (UTC+1)' },
    { value: 'Europe/Zurich', label: 'Europe/Zurich (UTC+1)' },
    { value: 'Europe/Stockholm', label: 'Europe/Stockholm (UTC+1)' },
    { value: 'Europe/Oslo', label: 'Europe/Oslo (UTC+1)' },
    { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (UTC+1)' },
    { value: 'Europe/Helsinki', label: 'Europe/Helsinki (UTC+2)' },
    { value: 'Europe/Athens', label: 'Europe/Athens (UTC+2)' },
    { value: 'Europe/Prague', label: 'Europe/Prague (UTC+1)' },
    { value: 'Europe/Warsaw', label: 'Europe/Warsaw (UTC+1)' },
    { value: 'Europe/Budapest', label: 'Europe/Budapest (UTC+1)' },
    { value: 'Europe/Dublin', label: 'Europe/Dublin (UTC+0)' },
    { value: 'Europe/Lisbon', label: 'Europe/Lisbon (UTC+0)' },
    { value: 'Europe/Istanbul', label: 'Europe/Istanbul (UTC+3)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },

    // North America
    { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
    { value: 'America/Chicago', label: 'America/Chicago (UTC-6)' },
    { value: 'America/Denver', label: 'America/Denver (UTC-7)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
    { value: 'America/Toronto', label: 'America/Toronto (UTC-5)' },
    { value: 'America/Vancouver', label: 'America/Vancouver (UTC-8)' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City (UTC-6)' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (UTC-3)' },
    { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (UTC-3)' },
    { value: 'America/Santiago', label: 'America/Santiago (UTC-3)' },
    { value: 'America/Lima', label: 'America/Lima (UTC-5)' },
    { value: 'America/Bogota', label: 'America/Bogota (UTC-5)' },
    { value: 'America/Caracas', label: 'America/Caracas (UTC-4)' },

    // Asia
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
    { value: 'Asia/Beijing', label: 'Asia/Beijing (UTC+8)' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (UTC+8)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (UTC+9)' },
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok (UTC+7)' },
    { value: 'Asia/Jakarta', label: 'Asia/Jakarta (UTC+7)' },
    { value: 'Asia/Manila', label: 'Asia/Manila (UTC+8)' },
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC+5:30)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+4)' },
    { value: 'Asia/Tel_Aviv', label: 'Asia/Tel_Aviv (UTC+2)' },
    { value: 'Asia/Riyadh', label: 'Asia/Riyadh (UTC+3)' },
    { value: 'Asia/Kuwait', label: 'Asia/Kuwait (UTC+3)' },
    { value: 'Asia/Qatar', label: 'Asia/Qatar (UTC+3)' },
    { value: 'Asia/Baghdad', label: 'Asia/Baghdad (UTC+3)' },
    { value: 'Asia/Tehran', label: 'Asia/Tehran (UTC+3:30)' },
    { value: 'Asia/Karachi', label: 'Asia/Karachi (UTC+5)' },
    { value: 'Asia/Dhaka', label: 'Asia/Dhaka (UTC+6)' },
    { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (UTC+7)' },
    { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok (UTC+10)' },

    // Oceania
    { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+10)' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne (UTC+10)' },
    { value: 'Australia/Perth', label: 'Australia/Perth (UTC+8)' },
    { value: 'Australia/Brisbane', label: 'Australia/Brisbane (UTC+10)' },
    { value: 'Australia/Adelaide', label: 'Australia/Adelaide (UTC+9:30)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (UTC+12)' },
    { value: 'Pacific/Fiji', label: 'Pacific/Fiji (UTC+12)' },
    { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (UTC-10)' },
    { value: 'Pacific/Guam', label: 'Pacific/Guam (UTC+10)' },

    // Africa
    { value: 'Africa/Cairo', label: 'Africa/Cairo (UTC+2)' },
    { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (UTC+2)' },
    { value: 'Africa/Lagos', label: 'Africa/Lagos (UTC+1)' },
    { value: 'Africa/Douala', label: 'Africa/Douala (UTC+1)' },
    { value: 'Africa/Casablanca', label: 'Africa/Casablanca (UTC+1)' },
    { value: 'Africa/Nairobi', label: 'Africa/Nairobi (UTC+3)' },
    { value: 'Africa/Algiers', label: 'Africa/Algiers (UTC+1)' },
    { value: 'Africa/Tunis', label: 'Africa/Tunis (UTC+1)' },
    { value: 'Africa/Dakar', label: 'Africa/Dakar (UTC+0)' },
    { value: 'Africa/Accra', label: 'Africa/Accra (UTC+0)' },
    { value: 'Africa/Khartoum', label: 'Africa/Khartoum (UTC+2)' },
    { value: 'Africa/Addis_Ababa', label: 'Africa/Addis_Ababa (UTC+3)' },

    // UTC and GMT
    { value: 'UTC', label: 'UTC (UTC+0)' },
    { value: 'GMT', label: 'GMT (UTC+0)' },
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2023)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2023)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2023-12-31)' },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-neutral-900">
          {t('settings.title', 'Settings')}
        </h1>
        <p className="text-neutral-600">
          {t(
            'settings.description',
            'Manage your account preferences and settings'
          )}
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Personal Information */}
        <div className="space-y-6">
          <h2 className="flex items-center gap-2 text-xl font-bold text-neutral-900">
            <User className="text-primary-600 h-5 w-5" />
            {t('settings.profile', 'Profile')}
          </h2>

          <div className="space-y-4">
            {/* User Info Card */}

            {/* Language Settings */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <Globe className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">
                      {t('settings.language', 'Language')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t(
                        'settings.languageDescription',
                        'Choose your preferred language'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isUpdating === 'language' && <UpdatingIndicator />}
                  <div className="w-32">
                    <Select
                      value={user.preferredLanguage}
                      onChange={handleLanguageChange}
                      options={languageOptions}
                      disabled={isUpdating === 'language'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Timezone Settings */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <Globe className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">
                      {t('settings.timezone', 'Timezone')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t(
                        'settings.timezoneDescription',
                        'Choose your timezone'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isUpdating === 'timezone' && <UpdatingIndicator />}
                  <div className="w-48">
                    <Select
                      value={user.timezone || 'Europe/Paris'}
                      onChange={handleTimezoneChange}
                      options={timezoneOptions}
                      disabled={isUpdating === 'timezone'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Date Format Settings */}
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                    <User className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">
                      {t('settings.dateFormat', 'Date format')}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {t(
                        'settings.dateFormatDescription',
                        'Choose your preferred date format'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isUpdating === 'dateFormat' && <UpdatingIndicator />}
                  <div className="w-48">
                    <Select
                      value={user.dateFormat || 'DD/MM/YYYY'}
                      onChange={handleDateFormatChange}
                      options={dateFormatOptions}
                      disabled={isUpdating === 'dateFormat'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications & Security */}
        <div className="space-y-6">
          {/* Notifications */}
          <div>
            <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-neutral-900">
              <Bell className="text-primary-600 h-5 w-5" />
              {t('settings.notifications', 'Notifications')}
            </h2>

            <div className="space-y-3">
              {/* Email Notifications */}
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                      <Bell className="h-5 w-5 text-neutral-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-neutral-900">
                        {t(
                          'profile.email_notifications',
                          'Notifications par email'
                        )}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.email_notifications_desc',
                          'Recevoir les alertes importantes par email'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUpdating === 'emailNotifications' && (
                      <UpdatingIndicator />
                    )}
                    <ToggleSwitch
                      checked={user.emailNotifications}
                      onChange={checked =>
                        handleNotificationToggle('email', checked)
                      }
                      disabled={isUpdating === 'emailNotifications'}
                      label="Recevoir les notifications par email"
                    />
                  </div>
                </div>

                <p className="mt-3 border-t border-neutral-100 px-2 pt-3 text-xs text-neutral-500">
                  {t(
                    'settings.dsnInAppOnly',
                    'Les alertes DSN à vérifier (retour rejeté ou avec anomalies, signalement non transmis) sont envoyées par email à l’adresse de notification de l’automatisation DSN du dossier, sinon au titulaire du domaine. Les autres alertes de suivi DSN restent dans l’application.'
                  )}
                </p>
              </div>

              {/* Push Notifications */}
              <div className="rounded-xl border border-neutral-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-50">
                      <Bell className="h-5 w-5 text-neutral-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-neutral-900">
                        {t('profile.push_notifications', 'Push notifications')}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(
                          'profile.push_notifications_desc',
                          'Receive push notifications'
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isUpdating === 'pushNotifications' && (
                      <UpdatingIndicator />
                    )}
                    <ToggleSwitch
                      checked={user.pushNotifications}
                      onChange={checked =>
                        handleNotificationToggle('push', checked)
                      }
                      disabled={isUpdating === 'pushNotifications'}
                      label="Recevoir les notifications push"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
